import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Package } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

interface PurchasedCreditsInfoProps {
  purchasedCredits?: number;
  creditsDetails?: Array<{
    id: string;
    credits_remaining: number;
    expires_at: string;
    purchased_at: string;
  }>;
}

export const PurchasedCreditsInfo = ({ 
  purchasedCredits = 0, 
  creditsDetails = [] 
}: PurchasedCreditsInfoProps) => {
  if (!purchasedCredits || creditsDetails.length === 0) {
    return null;
  }

  const formatCredits = (credits: number): string => {
    if (credits >= 1000000) {
      return `${(credits / 1000000).toFixed(1)}M`;
    } else if (credits >= 1000) {
      return `${(credits / 1000).toFixed(1)}K`;
    }
    return credits.toString();
  };

  const getExpirationStatus = (expiresAt: string) => {
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiration <= 3) {
      return { variant: "destructive" as const, text: "Expira em breve" };
    } else if (daysUntilExpiration <= 7) {
      return { variant: "secondary" as const, text: "Expira em breve" };
    }
    return { variant: "default" as const, text: "Válido" };
  };

  return (
    <Card className="p-4 bg-card border-border mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Package className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm">Créditos Comprados</h3>
        <Badge variant="outline" className="ml-auto">
          {formatCredits(purchasedCredits)} disponíveis
        </Badge>
      </div>

      <div className="space-y-2">
        {creditsDetails.map((credit) => {
          const status = getExpirationStatus(credit.expires_at);
          const expiresAt = new Date(credit.expires_at);
          
          return (
            <div
              key={credit.id}
              className="flex items-center justify-between p-2 rounded-lg bg-muted/50"
            >
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {formatCredits(credit.credits_remaining)} créditos
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  Expira {formatDistanceToNow(expiresAt, { 
                    addSuffix: true,
                    locale: ptBR 
                  })}
                </span>
                <Badge variant={status.variant} className="text-xs">
                  {status.text}
                </Badge>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground mt-3">
        💡 Créditos comprados expiram após 30 dias da data de compra
      </p>
    </Card>
  );
};
